import { TECHNOLOGY_CROSS_GRAFT_LIBRARY_IMPORTS } from '@/core/config/technologies'
import type { ClassData, LibraryData } from '@/core/types/library.type'
import { buildHostingLibraryContext } from '@/core/utils/graftability/buildHostingLibraryContext'
import { collectAssemblyNamesFromLibrary } from '@/core/utils/graftability/collectAssemblyNamesFromLibrary'
import { isGraftAssemblyReference } from '@/core/utils/graftability/isGraftAssemblyReference'
import { isHostingAssemblyReference } from '@/core/utils/graftability/isHostingAssemblyReference'
import { normalizeAssemblyName } from '@/core/utils/graftability/normalizeAssemblyName'
import { buildGcgNames, replacePlaceholders } from '@/core/utils/installation'

import { toGcgPlaceholders } from './computeGcgNames'

export interface CollectCrossGraftImportsParams {
  selectedTechnology: string
  libraryData?: LibraryData
  classData?: ClassData
  packageManager?: string
}

export const collectCrossGraftImports = ({
  selectedTechnology,
  libraryData,
  classData,
  packageManager,
}: CollectCrossGraftImportsParams): string[] => {
  if (!selectedTechnology || !libraryData) return []

  const template = TECHNOLOGY_CROSS_GRAFT_LIBRARY_IMPORTS[selectedTechnology]
  if (!template) return []

  const hostingLibraryContext = buildHostingLibraryContext(libraryData)
  const assemblyNames = collectAssemblyNamesFromLibrary(libraryData, classData)
  const crossGraftAssemblies = Array.from(assemblyNames)
    .map(normalizeAssemblyName)
    .filter((assemblyName) => isGraftAssemblyReference(assemblyName))
    .filter((assemblyName) => !isHostingAssemblyReference(assemblyName, hostingLibraryContext))
    .sort()

  const uniqueAssemblies = Array.from(new Set(crossGraftAssemblies))

  return uniqueAssemblies.map((assemblyName) => {
    const names = buildGcgNames({
      sourceRuntime: libraryData.runtimeName,
      libraryName: assemblyName,
      libraryNamespace: libraryData.namespace,
      consumerTechnology: selectedTechnology,
    })

    return replacePlaceholders(template, {
      ...toGcgPlaceholders(names),
      packageManager: packageManager ?? '',
      library: assemblyName,
      class: '',
    })
  })
}
